/**
 * "Where it's been used": how many drafts started from this document and how
 * many cases' agents read it, then the latest of those, each linking to the
 * draft or case. Lets a firm see which exemplars earn their place.
 */
import { BookOpen, FilePen } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { Link } from "react-router-dom";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { Spinner } from "@/components/ui/spinner";
import { caseWorkspacePath } from "@/features/caseIde/routes";
import { formatMdY } from "@/features/clientProfile/lib/format";

import { usageSummary } from "../model";
import type { EntryUse, FirmLibraryEntry } from "../types";

type Props = {
  entry: FirmLibraryEntry;
  scope: FirmScope;
};

const useLabel = (use: EntryUse): string =>
  use.kind === "draft"
    ? use.draft_title || "A draft"
    : "Read by the agent on a case";

const useLink = (use: EntryUse): string | null => {
  if (!use.individual_id) return null;
  return caseWorkspacePath(
    { individualId: use.individual_id },
    use.kind === "draft" && use.draft_id
      ? { kind: "draft", id: use.draft_id }
      : null
  );
};

export const EntryUsagePanel: FC<Props> = ({ entry, scope }) => {
  const used = (entry.draft_count ?? 0) + (entry.read_count ?? 0) > 0;
  const [uses, setUses] = useState<EntryUse[] | null>(null);

  useEffect(() => {
    if (!used) return undefined;
    let cancelled = false;
    firmKnowledgeApi
      .getEntryUsage(entry.id, scope)
      .then((data) => {
        if (!cancelled) setUses(data.uses);
      })
      .catch(() => {
        if (!cancelled) setUses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [entry.id, entry.last_used_at, used, scope]);

  return (
    <section className="space-y-2" data-testid="entry-usage">
      <h3 className="text-sm font-medium">Where It&apos;s Been Used</h3>
      <p className="text-sm text-fg-3" data-testid="entry-usage-summary">
        {usageSummary(entry)}
        {entry.last_used_at
          ? ` Last used ${formatMdY(entry.last_used_at)}.`
          : ""}
      </p>
      {used && uses === null ? (
        <div className="flex items-center gap-2 text-sm text-fg-3">
          <Spinner className="size-3" /> Loading…
        </div>
      ) : null}
      {uses?.length ? (
        <ul className="space-y-1" data-testid="entry-usage-list">
          {uses.map((use, i) => {
            const to = useLink(use);
            const Icon = use.kind === "draft" ? FilePen : BookOpen;
            const label = useLabel(use);
            return (
              <li
                // eslint-disable-next-line react/no-array-index-key
                key={`${use.kind}-${use.draft_id ?? use.individual_id}-${i}`}
                className="flex items-center gap-2 text-sm"
              >
                <Icon className="size-3.5 shrink-0 text-fg-3" />
                {to ? (
                  <Link
                    to={to}
                    className="min-w-0 truncate font-medium underline-offset-2 hover:underline"
                  >
                    {label}
                  </Link>
                ) : (
                  <span className="min-w-0 truncate">{label}</span>
                )}
                <span className="ml-auto shrink-0 text-fg-3">
                  {[use.created_by_name, formatMdY(use.created_at)]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
};
